/**
 * 系统设置页面
 * 包含通知设置：系统内通知、飞书通知配置
 * 管理员可配置飞书开放平台凭证
 */

import { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  App,
  Typography,
  Space,
  Row,
  Col,
  Switch,
  Spin,
  Divider,
  Alert,
} from "antd";
import {
  BellOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudServerOutlined,
  SaveOutlined,
  SendOutlined,
  SettingOutlined,
} from "../components/icons";
import { useTenantContext } from "../tenant/TenantContext";
import { trpc } from "../trpc";

const { Title, Text, Paragraph } = Typography;

interface NotificationSettings {
  systemNotificationEnabled: boolean;
  feishuNotificationEnabled: boolean;
  feishuWebhookUrl: string;
}

interface FeishuApiSettingsForm {
  feishuAppId: string;
  feishuAppSecret: string;
}

export function SystemSettings() {
  const { message } = App.useApp();
  const { context, isAdmin } = useTenantContext();
  const feishuApiModuleEnabled = Boolean(
    context?.features.script_monitor || context?.features.atmosphere_assistant
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [apiSaving, setApiSaving] = useState(false);
  const [apiTesting, setApiTesting] = useState(false);
  const [hasAppSecret, setHasAppSecret] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [apiTestResult, setApiTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [form] = Form.useForm();
  const [apiForm] = Form.useForm<FeishuApiSettingsForm>();

  // 监听飞书通知开关状态
  const feishuEnabled = Form.useWatch("feishuNotificationEnabled", form);

  // 加载设置
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await trpc.setting.getNotificationSettings.query();
        form.setFieldsValue(settings);

        if (isAdmin && feishuApiModuleEnabled) {
          const apiSettings = await trpc.setting.getFeishuApiSettings.query();
          setHasAppSecret(apiSettings.hasAppSecret);
          apiForm.setFieldsValue({
            feishuAppId: apiSettings.appId,
            feishuAppSecret: "",
          });
        }
      } catch (err) {
        message.error("加载设置失败");
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, [form, apiForm, isAdmin, feishuApiModuleEnabled, message]);

  // 保存设置
  const handleSave = async (values: NotificationSettings) => {
    setSaving(true);
    try {
      await trpc.setting.updateAllNotificationSettings.mutate({
        systemNotificationEnabled: values.systemNotificationEnabled,
        feishuNotificationEnabled: values.feishuNotificationEnabled,
        feishuWebhookUrl: values.feishuWebhookUrl || "",
      });
      message.success("设置保存成功！");
    } catch (err: any) {
      message.error(err?.message || "保存失败，请重试");
    } finally {
      setSaving(false);
    }
  };

  // 测试飞书通知
  const handleTestFeishu = async () => {
    const webhookUrl = form.getFieldValue("feishuWebhookUrl");
    if (!webhookUrl) {
      message.warning("请先输入飞书 Webhook URL");
      return;
    }

    setTesting(true);
    setTestResult(null);
    try {
      const result = await trpc.setting.testFeishuNotification.mutate({
        webhookUrl,
      });
      setTestResult({
        success: result.success,
        message: result.success
          ? "测试消息发送成功！请检查飞书群是否收到消息。"
          : `发送失败: ${result.body?.msg || result.body?.error || "未知错误"}`,
      });
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err?.message || "测试失败，请检查 Webhook URL 是否正确",
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveFeishuApi = async (values: FeishuApiSettingsForm) => {
    setApiSaving(true);
    try {
      await trpc.setting.updateFeishuApiSettings.mutate({
        appId: values.feishuAppId.trim(),
        appSecret: values.feishuAppSecret.trim() || undefined,
      });
      setHasAppSecret(true);
      apiForm.setFieldValue("feishuAppSecret", "");
      message.success("飞书开放平台配置保存成功！");
    } catch (err: any) {
      message.error(err?.message || "保存失败，请重试");
    } finally {
      setApiSaving(false);
    }
  };

  const handleTestFeishuApi = async () => {
    setApiTesting(true);
    setApiTestResult(null);
    try {
      const result = await trpc.setting.testFeishuApiConnection.mutate({});
      setApiTestResult({
        success: result.success,
        message: result.message,
      });
    } catch (err: any) {
      setApiTestResult({
        success: false,
        message: err?.message || "测试失败，请检查 App ID 和 App Secret",
      });
    } finally {
      setApiTesting(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fade-in">
      <Row gutter={[24, 24]}>
        {/* 通知设置 */}
        <Col xs={24} lg={16}>
          <Card
            variant="borderless"
            className="shadow-sm"
            title={
              <Space>
                <BellOutlined style={{ color: "#6366f1" }} />
                <span>通知设置</span>
              </Space>
            }
          >
            <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
              配置系统通知的推送方式，支持系统内通知和飞书群通知。
            </Text>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              initialValues={{
                systemNotificationEnabled: true,
                feishuNotificationEnabled: false,
                feishuWebhookUrl: "",
              }}
            >
              {/* 系统内通知 */}
              <Card
                size="small"
                style={{ marginBottom: 16, backgroundColor: "#fafafa" }}
              >
                <Row align="middle" justify="space-between">
                  <Col>
                    <Space vertical size={0}>
                      <Text strong>系统内通知</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        在系统内的通知中心显示消息
                      </Text>
                    </Space>
                  </Col>
                  <Col>
                    <Form.Item
                      name="systemNotificationEnabled"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* 飞书通知 */}
              <Card
                size="small"
                style={{ marginBottom: 16, backgroundColor: "#fafafa" }}
              >
                <Row align="middle" justify="space-between">
                  <Col>
                    <Space vertical size={0}>
                      <Text strong>飞书通知</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        通过飞书群机器人推送消息
                      </Text>
                    </Space>
                  </Col>
                  <Col>
                    <Form.Item
                      name="feishuNotificationEnabled"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>

                {/* 飞书配置区域 */}
                {feishuEnabled && (
                  <div style={{ marginTop: 16 }}>
                    <Divider style={{ margin: "16px 0" }} />
                    <Form.Item
                      name="feishuWebhookUrl"
                      label="Webhook URL"
                      rules={[
                        {
                          required: feishuEnabled,
                          message: "请输入飞书 Webhook URL",
                        },
                        {
                          type: "url",
                          message: "请输入有效的 URL 地址",
                        },
                      ]}
                      extra="在飞书群设置中添加自定义机器人获取 Webhook 地址"
                    >
                      <Input
                        placeholder="https://open.feishu.cn/open-apis/bot/v2/hook/xxx"
                        size="large"
                        allowClear
                      />
                    </Form.Item>

                    <Button
                      icon={<SendOutlined />}
                      onClick={handleTestFeishu}
                      loading={testing}
                      disabled={!form.getFieldValue("feishuWebhookUrl")}
                    >
                      发送测试消息
                    </Button>

                    {testResult && (
                      <Alert
                        style={{ marginTop: 12 }}
                        type={testResult.success ? "success" : "error"}
                        title={testResult.message}
                        icon={
                          testResult.success ? (
                            <CheckCircleOutlined />
                          ) : (
                            <CloseCircleOutlined />
                          )
                        }
                        showIcon
                        closable={{ onClose: () => setTestResult(null) }}
                      />
                    )}
                  </div>
                )}
              </Card>

              <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  size="large"
                  icon={<SaveOutlined />}
                >
                  保存设置
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {isAdmin && feishuApiModuleEnabled && (
            <Card
              variant="borderless"
              className="shadow-sm"
              style={{ marginTop: 24 }}
              title={
                <Space>
                  <CloudServerOutlined style={{ color: "#6366f1" }} />
                  <span>飞书开放平台</span>
                </Space>
              }
            >
              <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
                配置自建应用凭证，用于剧本助手读取和写入飞书电子表格（与群 Webhook 无关）。
              </Text>

              <Form
                form={apiForm}
                layout="vertical"
                onFinish={handleSaveFeishuApi}
                initialValues={{
                  feishuAppId: "",
                  feishuAppSecret: "",
                }}
              >
                <Form.Item
                  name="feishuAppId"
                  label="App ID"
                  rules={[{ required: true, message: "请输入 App ID" }]}
                  extra="在飞书开发者后台 → 应用 → 凭证与基础信息 中获取"
                >
                  <Input placeholder="cli_xxxxxxxx" size="large" allowClear />
                </Form.Item>

                <Form.Item
                  name="feishuAppSecret"
                  label="App Secret"
                  rules={[
                    {
                      validator: async (_, value) => {
                        if (hasAppSecret && !value) return;
                        if (!value?.trim()) {
                          throw new Error("请输入 App Secret");
                        }
                      },
                    },
                  ]}
                  extra={
                    hasAppSecret
                      ? "已配置 Secret，留空则保持不变"
                      : "首次配置必须填写 App Secret"
                  }
                >
                  <Input.Password placeholder="应用密钥" size="large" />
                </Form.Item>

                <Space wrap>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={apiSaving}
                    size="large"
                    icon={<SaveOutlined />}
                  >
                    保存配置
                  </Button>
                  <Button
                    icon={<SendOutlined />}
                    onClick={handleTestFeishuApi}
                    loading={apiTesting}
                  >
                    测试连接
                  </Button>
                </Space>

                {apiTestResult && (
                  <Alert
                    style={{ marginTop: 12 }}
                    type={apiTestResult.success ? "success" : "error"}
                    title={apiTestResult.message}
                    icon={
                      apiTestResult.success ? (
                        <CheckCircleOutlined />
                      ) : (
                        <CloseCircleOutlined />
                      )
                    }
                    showIcon
                    closable={{ onClose: () => setApiTestResult(null) }}
                  />
                )}
              </Form>
            </Card>
          )}
        </Col>

        {/* 帮助说明 */}
        <Col xs={24} lg={8}>
          <Card variant="borderless" className="shadow-sm">
            <Space vertical size="middle" style={{ width: "100%" }}>
              <div>
                <Title level={5} style={{ marginBottom: 8 }}>
                  <SettingOutlined style={{ marginRight: 8 }} />
                  通知说明
                </Title>
                <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 0 }}>
                  系统支持两种通知方式，您可以根据需要开启或关闭：
                </Paragraph>
              </div>

              <Divider style={{ margin: "8px 0" }} />

              <div>
                <Text strong>📬 系统内通知</Text>
                <Paragraph
                  type="secondary"
                  style={{ fontSize: 13, marginTop: 4, marginBottom: 0 }}
                >
                  通知会显示在系统右上角的通知中心，适合日常查看重要消息。
                </Paragraph>
              </div>

              <div>
                <Text strong>🔔 飞书通知</Text>
                <Paragraph
                  type="secondary"
                  style={{ fontSize: 13, marginTop: 4, marginBottom: 0 }}
                >
                  通过飞书群机器人实时推送，适合需要即时提醒的场景。
                </Paragraph>
              </div>

              <Divider style={{ margin: "8px 0" }} />

              <div>
                <Text strong>如何获取飞书 Webhook？</Text>
                <Paragraph
                  type="secondary"
                  style={{ fontSize: 13, marginTop: 4, marginBottom: 0 }}
                >
                  1. 打开飞书群聊设置
                  <br />
                  2. 选择「群机器人」
                  <br />
                  3. 添加「自定义机器人」
                  <br />
                  4. 复制 Webhook 地址
                </Paragraph>
              </div>

              {isAdmin && feishuApiModuleEnabled && (
                <>
                  <Divider style={{ margin: "8px 0" }} />
                  <div>
                    <Text strong>飞书表格 API 前置条件</Text>
                    <Paragraph
                      type="secondary"
                      style={{ fontSize: 13, marginTop: 4, marginBottom: 0 }}
                    >
                      1. 开发者后台开通 sheets:spreadsheet 权限并发布应用
                      <br />
                      2. 在目标表格「...」→「更多」→「添加文档应用」授权
                      <br />
                      3. 配置 App ID / App Secret 后，在剧本助手使用表格链接
                    </Paragraph>
                  </div>
                </>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
